import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Badge } from '@/components/data-display/badge'
import { useFeedback } from '@/components/feedback/feedback-context'
import { DrawerFormRow } from '@/components/forms/drawer-form-layout'
import { FormField } from '@/components/forms/form-field'
import { Input } from '@/components/forms/input'
import { ReadonlyField } from '@/components/forms/readonly-field'
import { Select } from '@/components/forms/select'
import { Drawer } from '@/components/overlays/drawer'
import { Button } from '@/components/primitives/button'
import { formatDateTime } from '@/features/admin/shared/format'

import { updateAdminAccount, type AdminAccount } from './api'

interface AdminAccountDrawerProps {
  admin: AdminAccount
  onOpenChange: (open: boolean) => void
  open: boolean
}

type AdminAccountErrors = Partial<Record<'name', string>>

function roleLabel(role: string, t: (key: string) => string) {
  if (role === 'super_admin') {
    return t('adminAccounts.role.superAdmin')
  }

  if (role === 'admin') {
    return t('adminAccounts.role.admin')
  }

  if (role === 'viewer') {
    return t('adminAccounts.role.viewer')
  }

  return role.trim() ? role : t('adminAccounts.role.unknown')
}

export function AdminAccountDrawer({ admin, onOpenChange, open }: AdminAccountDrawerProps) {
  const { t } = useTranslation('adminSettings')
  const { showFeedback } = useFeedback()
  const queryClient = useQueryClient()
  const [name, setName] = useState(admin.name)
  const [status, setStatus] = useState<AdminAccount['status']>(admin.status)
  const [errors, setErrors] = useState<AdminAccountErrors>({})
  const statusOptions = useMemo(
    () => [
      { label: t('adminAccounts.status.active'), value: 'active' },
      { label: t('adminAccounts.status.banned'), value: 'banned' },
    ],
    [t],
  )

  const updateMutation = useMutation({
    mutationFn: () => {
      const nextErrors: AdminAccountErrors = {}
      if (!name.trim()) {
        nextErrors.name = t('adminAccounts.drawer.validation.nameRequired')
      }
      if (Object.keys(nextErrors).length > 0) {
        setErrors(nextErrors)
        return Promise.resolve({ skipped: true })
      }

      setErrors({})
      return updateAdminAccount({
        adminId: admin.id,
        name,
        status,
      }).then(() => ({ skipped: false }))
    },
    onError: () => {
      showFeedback({
        message: t('errors.submitFailed'),
        tone: 'error',
      })
    },
    onSuccess: async (result) => {
      if (result.skipped) {
        return
      }

      await queryClient.invalidateQueries({ queryKey: ['admin-settings-admin-accounts'] })
      showFeedback({
        message: t('success.updateAdminAccount'),
        tone: 'success',
      })
      onOpenChange(false)
    },
  })

  return (
    <Drawer
      footer={
        <>
          <Button
            disabled={updateMutation.isPending}
            onClick={() => onOpenChange(false)}
            variant="secondary"
          >
            {t('actions.cancel')}
          </Button>
          <Button isLoading={updateMutation.isPending} onClick={() => updateMutation.mutate()}>
            {t('actions.save')}
          </Button>
        </>
      }
      onOpenChange={onOpenChange}
      open={open}
      size="md"
      title={t('adminAccounts.drawer.titleWithAdmin', { name: admin.name })}
    >
      <DrawerFormRow columns={1}>
        <FormField label={t('adminAccounts.drawer.email')}>
          <ReadonlyField>
            <span className="break-all">{admin.email}</span>
          </ReadonlyField>
        </FormField>
        <FormField
          error={errors.name}
          htmlFor="account-drawer-name"
          label={t('adminAccounts.drawer.name')}
          required
        >
          <Input
            id="account-drawer-name"
            invalid={Boolean(errors.name)}
            onChange={(event) => {
              setName(event.target.value)
              setErrors((current) => ({ ...current, name: undefined }))
            }}
            placeholder={t('adminAccounts.drawer.namePlaceholder')}
            value={name}
          />
        </FormField>
        <FormField label={t('adminAccounts.drawer.status')}>
          <Select
            aria-label={t('adminAccounts.drawer.status')}
            onValueChange={(value) => setStatus(value as AdminAccount['status'])}
            options={statusOptions}
            value={status}
          />
        </FormField>
        <FormField label={t('adminAccounts.drawer.role')}>
          <div className="flex min-h-[38px] items-center">
            <Badge variant={admin.role === 'super_admin' ? 'info' : 'neutral'}>
              {roleLabel(admin.role, t)}
            </Badge>
          </div>
        </FormField>
        <FormField label={t('adminAccounts.drawer.lastActiveAt')}>
          <ReadonlyField>{formatDateTime(admin.last_active_at)}</ReadonlyField>
        </FormField>
        <FormField label={t('adminAccounts.drawer.adminId')}>
          <ReadonlyField>
            <span className="break-all">{admin.id}</span>
          </ReadonlyField>
        </FormField>
      </DrawerFormRow>
    </Drawer>
  )
}
